import React, { useEffect, useState, useRef } from 'react';
import Pagination from "../components/Pagination.jsx";
import {
  Plus,
  Edit,
  Trash2,
  Calendar,
  Clock,
  Users,
  User,
  RefreshCw,
  AlertCircle,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Check
} from 'lucide-react';
import {
  AdminGetAllTrainerTimeSlots,
  AdminCreateTrainerTimeSlot,
  AdminUpdateTrainerTimeSlot,
  AdminDeleteTrainerTimeSlot,
  GetAllTrainer
} from '../components/Api.jsx';


const SearchableSelect = ({ options, value, onChange, placeholder, disabled, emptyText = "No results found" }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const wrapperRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const filteredOptions = options.filter(opt =>
    opt.label.toLowerCase().includes(search.toLowerCase())
  );

  const selectedOption = options.find(opt => String(opt.value) === String(value));

  return (
    <div className="relative" ref={wrapperRef}>
      <div
        className={`w-full px-2 py-1.5 border rounded text-sm bg-white cursor-pointer flex justify-between items-center border-gray-300 ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
        onClick={() => {
          if (!disabled) {
            setIsOpen(!isOpen);
            if (!isOpen) setSearch('');
          }
        }}
      >
        <span className={selectedOption ? 'text-black truncate pr-2' : 'text-gray-400 truncate pr-2'}>
          {selectedOption ? selectedOption.label : placeholder}
        </span>
        <svg className="w-4 h-4 text-gray-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path></svg>
      </div>

      {isOpen && !disabled && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded shadow-lg max-h-60 flex flex-col">
          <div className="p-2 border-b sticky top-0 bg-white">
            <input
              type="text"
              className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:border-blue-500 text-black"
              placeholder="Search..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onClick={(e) => e.stopPropagation()}
              autoFocus
            />
          </div>
          <div className="overflow-y-auto">
            {filteredOptions.length > 0 ? (
              filteredOptions.map(opt => (
                <div
                  key={opt.value}
                  className={`px-3 py-2 text-sm cursor-pointer hover:bg-gray-100 ${String(value) === String(opt.value) ? 'bg-blue-50 text-blue-600' : 'text-gray-700'}`}
                  onClick={() => {
                    onChange(opt.value);
                    setIsOpen(false);
                    setSearch('');
                  }}
                >
                  {opt.label}
                </div>
              ))
            ) : (
              <div className="px-3 py-2 text-sm text-gray-500 italic">
                {emptyText}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default function TrainerTimeSlotPage() {
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(6);

  const [timeSlots, setTimeSlots] = useState([]);
  const [trainers, setTrainers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [toast, setToast] = useState({ show: false, message: '', type: 'error' }); // Add toast state
  const [confirmModal, setConfirmModal] = useState({ show: false, title: '', message: '', onConfirm: null });

  const [form, setForm] = useState({
    trainerId: '',
    slotDate: '',
    startTime: '',
    endTime: '',
    sessionType: 'ONE_ON_ONE',
    sessionTopic: '',
    notes: '',
    maxUsers: 1,
    isRecurring: false,
    recurringPattern: 'WEEKLY',
    recurringEndDate: '',
    timezone: 'ASIA_KOLKATA',
    generateGoogleMeetLink: true
  });

  const todayDate = new Date().toISOString().split("T")[0];

  const getCurrentTime = () => {
    const now = new Date();
    const hours = String(now.getHours()).padStart(2, "0");
    const minutes = String(now.getMinutes()).padStart(2, "0");
    return `${hours}:${minutes}`;
  };


  // Add validation errors state
  const [errors, setErrors] = useState({});

  useEffect(() => {
    fetchTimeSlots();
    fetchTrainers();
  }, []);

  const fetchTimeSlots = async (isRefresh = false) => {
    if (isRefresh) {
      setRefreshing(true);
    } else {
      setLoading(true);
    }

    try {
      const response = await AdminGetAllTrainerTimeSlots();
      console.log('Time slots response:', response.data);
      setTimeSlots(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Error fetching time slots:', error);
      setTimeSlots([]);
    } finally {
      if (isRefresh) {
        setRefreshing(false);
      } else {
        setLoading(false);
      }
    }
  };

  const handleRefresh = () => {
    fetchTimeSlots(true);
    fetchTrainers();
  };

  const showToast = (message, type = 'success') => {
    setToast({ show: true, message, type });
    setTimeout(() => setToast({ show: false, message: '', type: 'error' }), 3000);
  };

  const fetchTrainers = async () => {
    try {
      const response = await GetAllTrainer();
      console.log('Trainers response:', response.data);

      // Handle different possible response structures
      let trainersData = [];
      if (Array.isArray(response.data)) {
        trainersData = response.data;
      } else if (response.data?.trainerCards && Array.isArray(response.data.trainerCards)) {
        trainersData = response.data.trainerCards;
      } else if (response.data?.data && Array.isArray(response.data.data)) {
        trainersData = response.data.data;
      } else if (response.data?.trainers && Array.isArray(response.data.trainers)) {
        trainersData = response.data.trainers;
      } else {
        console.log('Unexpected trainers response structure:', response.data);
        trainersData = [];
      }

      console.log('Processed trainers data:', trainersData);
      setTrainers(trainersData);
    } catch (error) {
      console.error('Error fetching trainers:', error);
      setTrainers([]);
    }
  };

  const resetForm = () => {
    setForm({
      trainerId: '',
      slotDate: '',
      startTime: '',
      endTime: '',
      sessionType: 'ONE_ON_ONE',
      sessionTopic: '',
      notes: '',
      maxUsers: 1,
      isRecurring: false,
      recurringPattern: 'WEEKLY',
      recurringEndDate: '',
      timezone: 'ASIA_KOLKATA',
      generateGoogleMeetLink: true
    });
    setEditingId(null);
    setShowForm(false);
    setErrors({}); // Reset errors
    setToast({ show: false, message: '', type: 'error' }); // Reset toast
  };

  const validateField = (fieldName, value) => {
    let error = '';

    // Create a proper "today at midnight" in local time
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    // Helper to create date from slotDate + time string (local)
    const combineDateTime = (dateStr, timeStr) => {
      if (!dateStr || !timeStr) return null;
      const [year, month, day] = dateStr.split('-').map(Number);
      const [hours, minutes] = timeStr.split(':').map(Number);
      return new Date(year, month - 1, day, hours, minutes);
    };

    switch (fieldName) {
      case 'trainerId':
        if (!value) {
          error = 'Trainer is required';
        }
        break;

      case 'sessionType':
        if (!value) {
          error = 'Session type is required';
        }
        break;

      case 'slotDate':
        if (!value) {
          error = 'Slot date is required';
        } else {
          const slotDate = new Date(value + 'T00:00:00'); // Local midnight
          if (slotDate < today) {
            error = 'Slot date must be today or a future date';
          }
        }
        break;

      case 'startTime':
        if (!value) {
          error = 'Start time is required';
        } else if (form.slotDate) {
          const selectedDateTime = combineDateTime(form.slotDate, value);
          const now = new Date();

          // If selected date is today, check if start time is in the past
          const selectedDateOnly = new Date(form.slotDate + 'T00:00:00');
          const isToday = selectedDateOnly.toDateString() === new Date().toDateString();

          if (isToday && selectedDateTime < now) {
            error = 'Start time cannot be in the past';
          }
        }
        break;

      case 'endTime':
        if (!value) {
          error = 'End time is required';
        } else if (form.startTime && form.slotDate) {
          const startDateTime = combineDateTime(form.slotDate, form.startTime);
          const endDateTime = combineDateTime(form.slotDate, value);

          if (endDateTime <= startDateTime) {
            error = 'End time must be after start time';
          }
        }
        break;

      case 'maxUsers':
        if (form.sessionType === 'COMBINED_CLASS' && (!value || value < 1)) {
          error = 'Max users is required for combined classes';
        }
        break;

      default:
        break;
    }

    return error;
  };

  // Update form field with real-time validation
  // const handleFormChange = (field, value) => {
  //   setForm({ ...form, [field]: value });

  //   // Validate the field immediately
  //   const error = validateField(field, value);
  //   setErrors({ ...errors, [field]: error });

  //   // If slot date changes, also re-validate start time since it depends on the slot date
  //   if (field === 'slotDate' && form.startTime) {
  //     const startTimeError = validateField('startTime', form.startTime);
  //     setErrors(prevErrors => ({ ...prevErrors, startTime: startTimeError }));
  //   }

  //   // If start time changes, also re-validate end time since it depends on start time
  //   if (field === 'startTime' && form.endTime) {
  //     const endTimeError = validateField('endTime', form.endTime);
  //     setErrors(prevErrors => ({ ...prevErrors, endTime: endTimeError }));
  //   }
  // };

  const handleFormChange = (field, value) => {
    setForm(prevForm => {
      const updatedForm = { ...prevForm, [field]: value };

      // Validate changed field
      const error = validateField(field, value);
      setErrors(prev => ({ ...prev, [field]: error }));

      // Revalidate start time when slotDate changes
      if (field === "slotDate" && updatedForm.startTime) {
        const startErr = validateField("startTime", updatedForm.startTime);
        setErrors(prev => ({ ...prev, startTime: startErr }));
      }

      // Revalidate end time when startTime changes
      if (field === "startTime" && updatedForm.endTime) {
        const endErr = validateField("endTime", updatedForm.endTime);
        setErrors(prev => ({ ...prev, endTime: endErr }));
      }

      // Revalidate maxUsers when sessionType changes
      if (field === "sessionType" && updatedForm.sessionType === 'COMBINED_CLASS') {
        if (!updatedForm.maxUsers || updatedForm.maxUsers < 1) {
          setErrors(prev => ({ ...prev, maxUsers: 'Max users is required for combined classes' }));
        }
      } else if (field === "sessionType") {
        // Clear maxUsers error when switching away from COMBINED_CLASS
        setErrors(prev => {
          const newErrors = { ...prev };
          delete newErrors.maxUsers;
          return newErrors;
        });
      }

      // Revalidate maxUsers when it changes and sessionType is COMBINED_CLASS
      if (field === "maxUsers" && updatedForm.sessionType === 'COMBINED_CLASS') {
        if (!value || value < 1) {
          setErrors(prev => ({ ...prev, maxUsers: 'Max users is required for combined classes' }));
        } else {
          setErrors(prev => {
            const newErrors = { ...prev };
            delete newErrors.maxUsers;
            return newErrors;
          });
        }
      }

      return updatedForm;
    });
  };


  const validateForm = () => {
    const newErrors = {};

    const combineDateTime = (dateStr, timeStr) => {
      if (!dateStr || !timeStr) return null;
      const [year, month, day] = dateStr.split('-').map(Number);
      const [hours, minutes] = timeStr.split(':').map(Number);
      return new Date(year, month - 1, day, hours, minutes);
    };

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    // Validate trainer
    if (!form.trainerId) {
      newErrors.trainerId = 'Trainer is required';
    }

    // Validate session type
    if (!form.sessionType) {
      newErrors.sessionType = 'Session type is required';
    }

    // Validate slot date
    if (!form.slotDate) {
      newErrors.slotDate = 'Slot date is required';
    } else {
      const slotDate = new Date(form.slotDate + 'T00:00:00');
      if (slotDate < today) {
        newErrors.slotDate = 'Slot date must be today or a future date';
      }
    }

    // Validate start time
    if (!form.startTime) {
      newErrors.startTime = 'Start time is required';
    } else if (form.slotDate) {
      const startDateTime = combineDateTime(form.slotDate, form.startTime);
      const now = new Date();
      const isToday = new Date(form.slotDate).toDateString() === new Date().toDateString();

      if (isToday && startDateTime < now) {
        newErrors.startTime = 'Start time cannot be in the past';
      }
    }

    // Validate end time
    if (!form.endTime) {
      newErrors.endTime = 'End time is required';
    } else if (form.startTime && form.slotDate) {
      const startDateTime = combineDateTime(form.slotDate, form.startTime);
      const endDateTime = combineDateTime(form.slotDate, form.endTime);

      if (endDateTime <= startDateTime) {
        newErrors.endTime = 'End time must be after start time';
      }
    }

    // Validate max users for combined class
    if (form.sessionType === 'COMBINED_CLASS' && (!form.maxUsers || form.maxUsers < 1)) {
      newErrors.maxUsers = 'Max users is required for combined classes';
    }

    if (form.isRecurring) {
      if (!form.recurringPattern) {
        newErrors.recurringPattern = 'Recurring pattern is required';
      }
      if (!form.recurringEndDate) {
        newErrors.recurringEndDate = 'End date is required for recurring slots';
      } else if (form.slotDate && form.recurringPattern) {
        const startDateObj = new Date(form.slotDate);
        const endDateObj = new Date(form.recurringEndDate);
        
        startDateObj.setHours(0, 0, 0, 0);
        endDateObj.setHours(0, 0, 0, 0);
        
        const diffTime = endDateObj.getTime() - startDateObj.getTime();
        const diffDays = Math.ceil(diffTime / (1000 * 3600 * 24));

        if (diffDays <= 0) {
          newErrors.recurringEndDate = 'End date must be after the slot date';
        } else if (form.recurringPattern === 'DAILY' && diffDays < 1) {
          newErrors.recurringEndDate = 'Daily recurring slots must have an end date at least 1 day after the slot date';
        } else if (form.recurringPattern === 'WEEKLY' && diffDays < 7) {
          newErrors.recurringEndDate = 'Weekly recurring slots must have an end date at least 1 week after the slot date';
        } else if (form.recurringPattern === 'MONTHLY') {
          const monthsDiff = (endDateObj.getFullYear() - startDateObj.getFullYear()) * 12 + (endDateObj.getMonth() - startDateObj.getMonth());
          const isSameDayOrLater = endDateObj.getDate() >= startDateObj.getDate();
          
          if (monthsDiff < 1 || (monthsDiff === 1 && !isSameDayOrLater)) {
            newErrors.recurringEndDate = 'Monthly recurring slots must have an end date at least 1 month after the slot date';
          }
        }
      }
    }

    setErrors(newErrors);
    return newErrors;
  };


  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate form before submission
    const currentErrors = validateForm();
    if (Object.keys(currentErrors).length > 0) {
      // Find the first error to display in toast
      let firstError = '';
      if (currentErrors.trainerId) firstError = currentErrors.trainerId;
      else if (currentErrors.sessionType) firstError = currentErrors.sessionType;
      else if (currentErrors.slotDate) firstError = currentErrors.slotDate;
      else if (currentErrors.startTime) firstError = currentErrors.startTime;
      else if (currentErrors.endTime) firstError = currentErrors.endTime;
      else if (currentErrors.maxUsers) firstError = currentErrors.maxUsers;
      else if (currentErrors.recurringPattern) firstError = currentErrors.recurringPattern;
      else if (currentErrors.recurringEndDate) firstError = currentErrors.recurringEndDate;
      else firstError = 'Please correct the errors in the form';

      showToast(firstError, 'error');

      return;
    }

    setLoading(true);
    try {
      // Combine date and time fields
      const startDateTime = form.slotDate && form.startTime ?
        `${form.slotDate}T${form.startTime}:00` : null;
      const endDateTime = form.slotDate && form.endTime ?
        `${form.slotDate}T${form.endTime}:00` : null;

      // Ensure proper data types and handle optional fields
      const payload = {
        trainerId: Number(form.trainerId),
        slotDate: form.slotDate,
        startTime: startDateTime,
        endTime: endDateTime,
        sessionType: form.sessionType,
        sessionTopic: form.sessionTopic || null,
        notes: form.notes || null,
        maxUsers: form.sessionType === 'COMBINED_CLASS' ? Number(form.maxUsers) : null,
        isRecurring: Boolean(form.isRecurring),
        recurringPattern: form.isRecurring ? form.recurringPattern : null,
        recurringEndDate: form.isRecurring && form.recurringEndDate ? form.recurringEndDate : null,
        timezone: form.timezone || 'ASIA_KOLKATA',
        generateGoogleMeetLink: Boolean(form.generateGoogleMeetLink)
      };

      console.log('Form data:', form);
      console.log('Payload being sent:', payload);
      console.log('Generate Google Meet Link:', form.generateGoogleMeetLink, '->', Boolean(form.generateGoogleMeetLink));

      if (editingId) {
        await AdminUpdateTrainerTimeSlot(editingId, payload);
      } else {
        await AdminCreateTrainerTimeSlot(payload);
      }

      await fetchTimeSlots();
      resetForm();

      showToast('Time slot saved successfully!');

      // Dispatch custom event for global refresh
      window.dispatchEvent(new Event('session-status-changed'));
    } catch (error) {
      console.error('Error saving time slot:', error);
      // More detailed error handling
      let errorMessage = 'Error saving time slot. Please try again.';

      // Check if it's a network error or server error with more details
      if (error.response) {
        // Server responded with error status
        if (error.response.data && error.response.data.message) {
          errorMessage = error.response.data.message;
        } else if (error.response.status === 400) {
          errorMessage = 'Invalid data provided. Please check your inputs.';
        } else if (error.response.status === 401) {
          errorMessage = 'Authentication failed. Please log in again.';
        } else if (error.response.status === 403) {
          errorMessage = 'Access denied. You do not have permission to perform this action.';
        } else if (error.response.status === 404) {
          errorMessage = typeof error.response.data === 'string' ? error.response.data : 'Resource not found.';
        } else if (error.response.status >= 500) {
          errorMessage = 'Server error. Please try again later.';
        }
      } else if (error.request) {
        // Network error
        errorMessage = 'Network error. Please check your connection and try again.';
      }

      showToast(errorMessage, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (timeSlot) => {
    setEditingId(timeSlot.id);

    // Extract time from datetime string (format: YYYY-MM-DDTHH:MM:SS)
    const extractTime = (datetime) => {
      if (!datetime) return '';
      // If it's already in HH:MM format, return as is
      if (datetime.match(/^\d{2}:\d{2}$/)) return datetime;
      // Extract time part from datetime string
      return datetime.substring(11, 16); // Gets HH:MM from YYYY-MM-DDTHH:MM:SS
    };

    setForm({
      trainerId: timeSlot.trainerId?.toString() || '',
      slotDate: timeSlot.slotDate || '',
      startTime: extractTime(timeSlot.startTime),
      endTime: extractTime(timeSlot.endTime),
      sessionType: timeSlot.sessionType || 'ONE_ON_ONE',
      sessionTopic: timeSlot.sessionTopic || '',
      notes: timeSlot.notes || '',
      maxUsers: timeSlot.maxUsers || 1,
      isRecurring: Boolean(timeSlot.isRecurring),
      recurringPattern: timeSlot.recurringPattern || 'WEEKLY',
      recurringEndDate: timeSlot.recurringEndDate || '',
      timezone: timeSlot.timezone || 'ASIA_KOLKATA',
      generateGoogleMeetLink: Boolean(timeSlot.generateGoogleMeetLink)
    });
    setShowForm(true);
  };

  const handleDelete = (timeSlot) => {
    setConfirmModal({
      show: true,
      title: 'Confirm Delete',
      message: `Are you sure you want to delete this time slot for ${timeSlot.trainerName || 'this trainer'}? This action cannot be undone.`,
      onConfirm: async () => {
        try {
          await AdminDeleteTrainerTimeSlot(timeSlot.id);
          await fetchTimeSlots();

          // Dispatch custom event for global refresh
          window.dispatchEvent(new Event('session-status-changed'));
          showToast('Time slot deleted successfully!');
        } catch (error) {
          console.error('Error deleting time slot:', error);
          showToast('Error deleting time slot. Please try again.', 'error');
        } finally {
          setConfirmModal(prev => ({ ...prev, show: false }));
        }
      }
    });
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'AVAILABLE':
        return <CheckCircle className="w-4 h-4 text-green-500" />;
      case 'BOOKED':
        return <XCircle className="w-4 h-4 text-red-500" />;
      case 'CANCELLED':
        return <AlertCircle className="w-4 h-4 text-yellow-500" />;
      default:
        return <Clock className="w-4 h-4 text-gray-500" />;
    }
  };

  const getSessionTypeIcon = (sessionType) => {
    return sessionType === 'ONE_ON_ONE' ?
      <User className="w-4 h-4 text-gray-400" /> :
      <Users className="w-4 h-4 text-gray-400" />;
  };

  const getMinEndDate = () => {
    if (!form.slotDate) return '';
    const localDate = new Date(form.slotDate + 'T00:00:00');
    
    if (form.recurringPattern === 'DAILY') {
      localDate.setDate(localDate.getDate() + 1);
    } else if (form.recurringPattern === 'WEEKLY') {
      localDate.setDate(localDate.getDate() + 7);
    } else if (form.recurringPattern === 'MONTHLY') {
      localDate.setMonth(localDate.getMonth() + 1);
    } else {
      localDate.setDate(localDate.getDate() + 1);
    }
    
    const year = localDate.getFullYear();
    const month = String(localDate.getMonth() + 1).padStart(2, '0');
    const day = String(localDate.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  return (
    <div className="min-h-screen p-4">
      {/* Toast Notification */}
      {toast.show && (
        <div className={`fixed top-4 right-4 z-50 p-4 rounded-lg shadow-lg ${toast.type === 'error' ? 'bg-red-500 text-white' : 'bg-green-500 text-white'
          }`}>
          <div className="flex items-center gap-2">
            {toast.type === 'error' ? <AlertCircle className="w-5 h-5" /> : <Check className="w-5 h-5" />}
            {toast.message}
          </div>
        </div>
      )}
      {/* Header */}
      <div className="mb-6">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex flex-col lg:flex-row justify-between gap-6">
            <div className="flex-1">
              <div className="flex items-start gap-4">
                <div className="bg-cyan-100 p-3 rounded-lg">
                  <Calendar className="w-6 h-6 text-cyan-600" />
                </div>
                <div>
                  <p className="text-gray-600 text-sm leading-relaxed max-w-3xl">
                    Create time slots available to the users with the assigned trainer. Manage trainer availability, session scheduling, and booking management.
                  </p>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <button
                onClick={handleRefresh}
                disabled={refreshing}
                className="flex items-center gap-2 px-4 py-2.5 bg-green-600 hover:bg-green-700 disabled:bg-green-400 text-white rounded-lg transition-all duration-200 disabled:cursor-not-allowed text-sm font-medium shadow-sm hover:shadow focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:ring-offset-2"
                aria-label={refreshing ? "Refreshing data" : "Refresh time slots"}
              >
                <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
                <span>{refreshing ? 'Refreshing...' : 'Refresh'}</span>
              </button>
              <button
                onClick={() => setShowForm(true)}
                className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2.5 rounded-lg flex items-center gap-2 transition-colors text-sm font-medium shadow-sm hover:shadow focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
              >
                <Plus className="w-4 h-4" />
                Create Time Slot
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Create/Edit Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-gray-900">
                  {editingId ? 'Edit Time Slot' : 'Create New Time Slot'}
                </h2>
                <button
                  onClick={resetForm}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XCircle className="w-6 h-6" />
                </button>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                {/* Compact row with 3 fields */}
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      Trainer *
                    </label>
                    <SearchableSelect
                      options={Array.isArray(trainers) ? trainers.map(t => ({ value: t.id, label: t.trainerName })) : []}
                      value={form.trainerId}
                      onChange={(val) => handleFormChange('trainerId', val)}
                      placeholder="Select Trainer"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      Session Type *
                    </label>
                    <select
                      value={form.sessionType}
                      onChange={(e) => handleFormChange('sessionType', e.target.value)}
                      className="w-full text-black px-2 py-1.5 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      required
                    >
                      <option value="" disabled hidden>Select Session Type</option>
                      <option value="ONE_ON_ONE">One-on-One</option>
                      <option value="COMBINED_CLASS">Combined Class</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      Slot Date *
                    </label>
                    <input
                      type="date"
                      value={form.slotDate}
                      onChange={(e) => handleFormChange('slotDate', e.target.value)}
                      min={new Date().toISOString().split('T')[0]}
                      className="w-full text-black px-2 py-1.5 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      required
                    />
                  </div>
                </div>

                {/* Compact row with 3 fields */}
                <div className="grid grid-cols-3 gap-3">
                  {/* Start Time */}
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      Start Time *
                    </label>
                    {errors.startTime && (
                      <p className="mt-1 text-xs text-red-600">{errors.startTime}</p>
                    )}
                    <input
                      type="time"
                      value={form.startTime}
                      onChange={(e) => {
                        // Additional validation for start time when slot date is today
                        if (form.slotDate === todayDate) {
                          const selectedTime = e.target.value;
                          const currentTime = getCurrentTime();

                          // Allow start time if it's not in the past
                          if (selectedTime >= currentTime) {
                            handleFormChange('startTime', selectedTime);
                          } else {
                            // Show validation error
                            setErrors(prev => ({ ...prev, startTime: 'Start time cannot be in the past' }));
                          }
                        } else {
                          handleFormChange('startTime', e.target.value);
                        }
                      }}
                      min={form.slotDate === todayDate ? getCurrentTime() : "00:00"}
                      className={`w-full text-black px-2 py-1.5 border rounded text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent ${errors.startTime ? 'border-red-500' : 'border-gray-300'
                        }`}
                      required
                    />

                  </div>

                  {/* End Time */}
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      End Time *
                    </label>
                    {errors.endTime && (
                      <p className="mt-1 text-xs text-red-600">{errors.endTime}</p>
                    )}
                    <input
                      type="time"
                      value={form.endTime}
                      onChange={(e) => {
                        // Additional validation for end time to ensure it's after start time
                        if (form.startTime) {
                          const selectedEndTime = e.target.value;

                          // Allow end time if it's after start time
                          if (selectedEndTime > form.startTime) {
                            handleFormChange('endTime', selectedEndTime);
                          } else {
                            // Show validation error
                            setErrors(prev => ({ ...prev, endTime: 'End time must be after start time' }));
                          }
                        } else {
                          handleFormChange('endTime', e.target.value);
                        }
                      }}
                      min={form.startTime || "00:00"}
                      className={`w-full text-black px-2 py-1.5 border rounded text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent ${errors.endTime ? 'border-red-500' : 'border-gray-300'
                        }`}
                      required
                    />

                  </div>

                  {form.sessionType === 'COMBINED_CLASS' ? (
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">
                        Max Users *
                      </label>
                      <input
                        type="number"
                        min="1"
                        max="50"
                        value={form.maxUsers}
                        onChange={(e) => handleFormChange('maxUsers', e.target.value)}
                        className="w-full text-black px-2 py-1.5 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        required
                      />
                    </div>
                  ) : (
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">
                        Timezone
                      </label>
                      <select
                        value={form.timezone}
                        onChange={(e) => handleFormChange('timezone', e.target.value)}
                        className="w-full text-black px-2 py-1.5 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      >
                        <option value="" disabled hidden>Select Timezone</option>
                        <option value="ASIA_KOLKATA">IST</option>
                        <option value="UTC">UTC</option>
                        <option value="AMERICA_NEW_YORK">EST</option>
                        <option value="EUROPE_LONDON">GMT</option>
                      </select>
                    </div>
                  )}
                </div>

                {/* Session topic - appears for both session types */}
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Session Topic
                  </label>
                  <input
                    type="text"
                    value={form.sessionTopic}
                    onChange={(e) => handleFormChange('sessionTopic', e.target.value)}
                    placeholder="Topic"
                    className="w-full text-black px-2 py-1.5 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Notes
                  </label>
                  <textarea
                    value={form.notes}
                    onChange={(e) => handleFormChange('notes', e.target.value)}
                    rows={2}
                    placeholder="Additional notes..."
                    className="w-full text-black px-2 py-1.5 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                <div className="flex items-center gap-4">
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={form.isRecurring}
                      onChange={(e) => setForm({ ...form, isRecurring: e.target.checked })}
                      className="rounded text-black border-gray-300 focus:ring-blue-500"
                    />
                    <span className="text-xs font-medium text-gray-700">Recurring</span>
                  </label>

                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={form.generateGoogleMeetLink}
                      onChange={(e) => setForm({ ...form, generateGoogleMeetLink: e.target.checked })}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <span className="text-xs font-medium text-gray-700">Google Meet</span>
                  </label>
                </div>

                {form.generateGoogleMeetLink && (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                    <div className="flex items-start gap-2">
                      <div className="text-blue-600 mt-0.5">
                        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                        </svg>
                      </div>
                      <div className="text-xs text-blue-800">
                        <p className="font-medium">Google Meet Integration</p>
                        <ul className="mt-1 space-y-1">
                          {form.sessionType === 'ONE_ON_ONE' ? (
                            <>
                              <li>• Link generated for session</li>
                              <li>• Users request from mobile app</li>
                              <li>• Link sent after admin approval</li>
                            </>
                          ) : (
                            <>
                              <li>• Link generated for session</li>
                              <li>• Sent to registered users immediately</li>
                              <li>• Users join directly without approval</li>
                            </>
                          )}
                        </ul>
                      </div>
                    </div>
                  </div>
                )}

                {form.isRecurring && (
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">
                        Recurring Pattern
                      </label>
                      <select
                        value={form.recurringPattern}
                        onChange={(e) => handleFormChange('recurringPattern', e.target.value)}
                        className="w-full text-black px-2 py-1.5 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      >
                        <option value="" disabled hidden>Select Pattern</option>
                        <option value="DAILY">Daily</option>
                        <option value="WEEKLY">Weekly</option>
                        <option value="MONTHLY">Monthly</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">
                        End Date
                      </label>
                      <input
                        type="date"
                        value={form.recurringEndDate}
                        min={getMinEndDate()}
                        onChange={(e) => handleFormChange('recurringEndDate', e.target.value)}
                        className="w-full text-black px-2 py-1.5 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                  </div>
                )}

                <div className="flex justify-end gap-2 pt-3 border-t">
                  <button
                    type="button"
                    onClick={resetForm}
                    className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors text-sm"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={loading}
                    className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors disabled:opacity-50 text-sm"
                  >
                    {loading ? 'Saving...' : (editingId ? 'Update' : 'Create')}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Time Slots Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {loading && timeSlots.length === 0 ? (
          <div className="col-span-full flex justify-center items-center py-12">
            <div className="text-center">
              <RefreshCw className="w-8 h-8 text-gray-400 animate-spin mx-auto mb-4" />
              <p className="text-gray-500">Loading time slots...</p>
            </div>
          </div>
        ) : timeSlots.length === 0 ? (
          <div className="col-span-full text-center py-12">
            <Calendar className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No time slots found</h3>
            <p className="text-gray-500">Create your first time slot to get started.</p>
          </div>
        ) : (
          timeSlots.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage).map((timeSlot) => (
            <div key={timeSlot.id} className="bg-white rounded-lg shadow-sm border hover:shadow-md transition-shadow">
              <div className="p-6">
                <div className="flex justify-between items-start mb-4">
                  <div className="flex items-center gap-2">
                    {getSessionTypeIcon(timeSlot.sessionType)}
                    <span className="text-sm font-medium text-gray-600">
                      {timeSlot.sessionType === 'ONE_ON_ONE' ? 'One-on-One' : 'Combined Class'}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    {getStatusIcon(timeSlot.status)}
                    <span className="text-sm text-gray-600">{timeSlot.status}</span>

                  </div>

                </div>
                <div className='flex justify-between w-full'>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">
                    {timeSlot.sessionTopic || 'Training Session'}

                  </h3>
                  {timeSlot.googleMeetLinkGenerated && (
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                      Google Meet
                    </span>
                  )}
                </div>

                <div className="space-y-2 text-sm text-gray-600">
                  <div className="flex items-center gap-2">
                    <User className="w-4 h-4" />
                    <span>{timeSlot.trainerName}</span>
                  </div>

                  <div className="flex items-center gap-2">
                    <Calendar className="w-4 h-4" />
                    <span>{new Date(timeSlot.slotDate).toLocaleDateString()}</span>
                  </div>

                  <div className="flex items-center gap-2">
                    <Clock className="w-4 h-4" />
                    <span>
                      {new Date(timeSlot.startTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })} -
                      {new Date(timeSlot.endTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    </span>
                  </div>


                  {timeSlot.sessionType === 'COMBINED_CLASS' && (
                    <div className="flex items-center gap-2">
                      <Users className="w-4 h-4" />
                      <span>
                        {timeSlot.currentRegisteredUsers || 0} / {timeSlot.maxUsers} users
                      </span>
                    </div>
                  )}

                  {timeSlot.notes && (
                    <p className="text-xs text-gray-500 mt-2 line-clamp-2">
                      {timeSlot.notes}
                    </p>
                  )}

                  {timeSlot.sessionType === 'ONE_ON_ONE' && timeSlot.requiresApproval && (
                    <div className="flex items-center gap-2 text-sm">
                      <span className="text-gray-600">Approval Status:</span>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${timeSlot.approvalStatus === 'PENDING' ? 'bg-yellow-100 text-yellow-800' :
                        timeSlot.approvalStatus === 'APPROVED' ? 'bg-green-100 text-green-800' :
                          timeSlot.approvalStatus === 'REJECTED' ? 'bg-red-100 text-red-800' :
                            'bg-gray-100 text-gray-800'
                        }`}>
                        {timeSlot.approvalStatus || 'PENDING'}
                      </span>
                    </div>
                  )}

                  {timeSlot.meetingLink && (
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                      <div className="flex items-center gap-2 text-sm text-blue-800 mb-2">
                        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                          <path d="M2 6a2 2 0 012-2h6l2 2h6a2 2 0 012 2v6a2 2 0 01-2 2H4a2 2 0 01-2-2V6zM14.553 7.106A1 1 0 0014 8v4a1 1 0 00.553.894l2 1A1 1 0 0018 13V7a1 1 0 00-1.447-.894l-2 1z" />
                        </svg>
                        <span className="font-medium">Google Meet Link</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <a
                          href={timeSlot.meetingLink}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-600 hover:text-blue-800 text-sm underline flex-1"
                        >
                          {timeSlot.meetingLink}
                        </a>
                        <button
                          onClick={() => navigator.clipboard.writeText(timeSlot.meetingLink)}
                          className="text-blue-600 hover:text-blue-800 p-1"
                          title="Copy link"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                          </svg>
                        </button>
                      </div>
                    </div>
                  )}
                </div>

                <div className="flex gap-2 mt-4">
                  <button
                    onClick={() => handleEdit(timeSlot)}
                    className="flex-1 bg-blue-50 hover:bg-blue-100 text-blue-600 px-3 py-2 rounded-lg text-sm font-medium transition-colors flex items-center justify-center gap-1"
                  >
                    <Edit className="w-4 h-4" />
                    Edit
                  </button>
                  <button
                    onClick={() => handleDelete(timeSlot)}
                    className="flex-1 bg-red-50 hover:bg-red-100 text-red-600 px-3 py-2 rounded-lg text-sm font-medium transition-colors flex items-center justify-center gap-1"
                  >
                    <Trash2 className="w-4 h-4" />
                    Delete
                  </button>
                </div>
              </div>
            </div>
          ))
        )}
      </div>
      {/* Custom Confirm Modal */}
      {confirmModal.show && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4 backdrop-blur-sm transition-all duration-300">
          <div className="bg-white rounded-2xl max-w-sm w-full shadow-2xl transform transition-all duration-300 scale-100 p-6 border border-gray-100">
            <div className="flex flex-col items-center text-center">
              <div className="bg-red-50 p-4 rounded-full mb-4">
                <AlertTriangle className="w-8 h-8 text-red-500" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">
                {confirmModal.title}
              </h3>
              <p className="text-gray-600 text-sm leading-relaxed mb-8">
                {confirmModal.message}
              </p>
              <div className="flex gap-3 w-full">
                <button
                  onClick={() => setConfirmModal(prev => ({ ...prev, show: false }))}
                  className="flex-1 px-4 py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl transition-colors font-semibold text-sm focus:outline-none focus:ring-2 focus:ring-gray-300"
                >
                  Cancel
                </button>
                <button
                  onClick={confirmModal.onConfirm}
                  className="flex-1 px-4 py-2.5 bg-red-500 hover:bg-red-600 text-white rounded-xl shadow-lg shadow-red-200 transition-all duration-200 font-semibold text-sm focus:outline-none focus:ring-2 focus:ring-red-400 focus:ring-offset-2"
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
          {timeSlots?.length > 0 && (

      <Pagination 
          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
          totalPages={Math.ceil((Array.isArray(timeSlots) ? timeSlots.length : (timeSlots?.length || 0)) / itemsPerPage)}
          totalElements={(Array.isArray(timeSlots) ? timeSlots.length : (timeSlots?.length || 0))}
          pageSize={itemsPerPage}
          setPageSize={setItemsPerPage}
        />
      )}
</div>
  );
}